import React, { useState, useEffect } from "react";
import { Modal } from "@/components/ui/Modal";
import {
  CompetitionProfile,
  CompetitionPlatform,
  ProfileType,
  CreateCompetitionProfileRequest,
  UpdateCompetitionProfileRequest,
  PLATFORM_CONFIG,
  PROFILE_TYPE_CONFIG,
} from "../types";

interface CompetitionProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateCompetitionProfileRequest | UpdateCompetitionProfileRequest
  ) => Promise<void>;
  profile?: CompetitionProfile | null;
  isLoading?: boolean;
}

const PLATFORMS: CompetitionPlatform[] = [
  "instagram",
  "tiktok",
  "website",
  "facebook",
  "youtube",
];

const PROFILE_TYPES: ProfileType[] = [
  "competitor",
  "collaborator",
  "influencer",
  "inspiration",
  "partner",
];

export const CompetitionProfileModal: React.FC<CompetitionProfileModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  profile,
  isLoading = false,
}) => {
  const [profileType, setProfileType] = useState<ProfileType>("competitor");
  const [platform, setPlatform] = useState<CompetitionPlatform>("instagram");
  const [displayName, setDisplayName] = useState("");
  const [handle, setHandle] = useState("");
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!profile;

  useEffect(() => {
    if (profile) {
      setProfileType(profile.profileType);
      setPlatform(profile.platform);
      setDisplayName(profile.displayName);
      setHandle(profile.handle || "");
      setUrl(profile.url || "");
      setNotes(profile.notes || "");
      setTagsInput(profile.tags.join(", "));
    } else {
      setProfileType("competitor");
      setPlatform("instagram");
      setDisplayName("");
      setHandle("");
      setUrl("");
      setNotes("");
      setTagsInput("");
    }
    setError(null);
  }, [profile, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!displayName.trim()) {
      setError("Display name is required");
      return;
    }

    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    try {
      if (isEditing) {
        const data: UpdateCompetitionProfileRequest = {
          profileType,
          displayName: displayName.trim(),
          handle: handle.trim() || undefined,
          url: url.trim() || undefined,
          notes: notes.trim() || undefined,
          tags,
        };
        await onSubmit(data);
      } else {
        const data: CreateCompetitionProfileRequest = {
          profileType,
          platform,
          displayName: displayName.trim(),
          handle: handle.trim() || undefined,
          url: url.trim() || undefined,
          notes: notes.trim() || undefined,
          tags,
        };
        await onSubmit(data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save profile");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit Competition Profile" : "Add Competition Profile"}
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="competition-profile-form"
            disabled={isLoading}
            className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving...
              </>
            ) : isEditing ? (
              "Save Changes"
            ) : (
              "Add Profile"
            )}
          </button>
        </>
      }
    >
      <form
        id="competition-profile-form"
        onSubmit={handleSubmit}
        className="p-6 space-y-4"
      >
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            {error}
          </div>
        )}

        {/* Profile Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Profile Type
          </label>
          <select
            value={profileType}
            onChange={(e) => setProfileType(e.target.value as ProfileType)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {PROFILE_TYPES.map((pt) => (
              <option key={pt} value={pt}>
                {PROFILE_TYPE_CONFIG[pt].label}
              </option>
            ))}
          </select>
        </div>

        {/* Platform */}
        {!isEditing && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Platform
            </label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as CompetitionPlatform)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {PLATFORM_CONFIG[p].label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Display Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Competitor Coffee Shop"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Handle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Handle / Username
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              @
            </span>
            <input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value.replace(/^@/, ""))}
              placeholder="username"
              className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* URL */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Profile URL
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://instagram.com/username"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tags
          </label>
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="local, premium, competitor (comma separated)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Separate tags with commas
          </p>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any notes about this competitor..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          />
        </div>
      </form>
    </Modal>
  );
};
